/**
 * Admin → Users tab — feature E05-F01 (E05-F01-T03 + E05-F01-T04).
 *
 * 1:1 port of the legacy `dashboard.js` users UI (loadUsers / renderUsersList /
 * openUserDialog / saveUser / deleteUser / disconnectOutlook) + `index.html`
 * #view-users + #user-dialog. It is the admin Settings tab that LISTS the agency
 * users and offers full CRUD plus IMAP/Outlook mailbox management.
 *
 * T03 — the table (renderUsersList):
 *   - columns: User (avatar bubble + name, with a `(you)` tag on the current
 *     user), Email, Role (a coloured badge — admin=purple / teamlead=blue /
 *     recruiter=gray), Team, Outlook (Connected ✓ + Disconnect, or "—"), Actions
 *     (Edit always; Delete hidden on the current user's own row — acceptance #2);
 *   - the avatar bubble, role label/colour and self-detection come from the pure
 *     `usersModel.ts` (unit-tested), so the rendering rules can never drift.
 *
 * T04 — the dialog mailbox section (openUserDialog / saveUser):
 *   - the Add/Edit dialog carries name / email / password ("Leave empty to keep
 *     current" on Edit) / role / team / avatar / email signature, plus a
 *     collapsible IMAP/SMTP "Email account" section;
 *   - on Edit the mailbox fields are seeded from `user.mailbox` and the section
 *     starts EXPANDED iff an imap_host is already configured (acceptance #3);
 *   - Save sends `mailbox` ONLY when imap_host is filled (acceptance #1); a blank
 *     imap_host omits `mailbox` so the imap row is left untouched (acceptance #2).
 *
 * E10-F04-T10 — the signature the dialog shows:
 *   - the saved `email_signature` is NOT part of the list DTO (it carries the logo
 *     as a ~256 KB `data:` URI), so the OPEN Edit dialog fetches just that one value
 *     (`useUserSignature`) and `applyLoadedSignature` seeds the editor with it —
 *     reopening Settings → Users → Edit shows the signature instead of a blank field;
 *   - until the response lands the editor is a blank PLACEHOLDER (disabled, with a
 *     "loading" hint), and `bodyFromForm` omits `email_signature` entirely, so saving
 *     an unrelated field while the fetch is in flight (or after it failed) cannot
 *     write that blank over the stored signature.
 *
 * Transport: useUsers / useUserSignature / useCreateUser / useUpdateUser /
 * useDeleteUser / useDisconnectOutlook (usersApi.ts) → the shared apiClient. The list is
 * PASSWORD-FREE (the API strips every secret), so no password ever reaches the
 * DOM (T01 #1 / T02 #3 — the e2e asserts this).
 *
 * Self-row detection needs the signed-in principal: the tab takes `currentUserId`
 * as a prop (the Settings view passes `useAuth().user?.id`) — keeping the tab a
 * pure presenter that is trivially testable.
 */
import { useEffect, useMemo, useState } from 'react';
import type { AdminUserDTO } from '@eer/shared';
import { ADMIN_USER_ROLES } from '@eer/shared';
import { Dialog, DialogFooter, RichTextEditor, useToast } from '../ui/index.js';
import {
  avatarLabel,
  roleLabel,
  roleBadgeClass,
  isSelf,
  formFromUser,
  applyLoadedSignature,
  editedSignature,
  mailboxIsExpanded,
  bodyFromForm,
  type UserForm,
  type UserMailboxForm,
} from './usersModel.js';
import { errorMessage } from '../api/client.js';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useUserSignature,
} from './usersApi.js';
import { useTeams } from './teamsApi.js';
import { OutlookConnect } from './OutlookConnect.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

// ---------------------------------------------------------------------------
// Role badge (renderUsersList roleBadge)
// ---------------------------------------------------------------------------

/** The pill badge shown in the role column of a user row. */
export function RoleBadge({ role }: { readonly role: string }): React.JSX.Element {
  return (
    <span
      data-testid="user-role-badge"
      data-role={role}
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${roleBadgeClass(role)}`}
    >
      {roleLabel(role)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit dialog (#user-dialog)
// ---------------------------------------------------------------------------

interface UserDialogProps {
  /** The user being edited, or null for an Add. */
  readonly user: AdminUserDTO | null;
  readonly onClose: () => void;
}

function UserDialog({ user, onClose }: UserDialogProps): React.JSX.Element {
  const toast = useToast();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  // Defect fix: the Team field is a DROPDOWN of EXISTING recruiter teams (reuses
  // the same useTeams() hook RecruiterTeamsTab uses). Membership is keyed by team
  // NAME (users.team_id = teams.name), so the option value is the team name.
  const teamsQuery = useTeams();
  const [form, setForm] = useState<UserForm>(() => formFromUser(user));
  const [mailboxOpen, setMailboxOpen] = useState<boolean>(() => mailboxIsExpanded(user));

  const isEdit = user != null;
  const pending = createUser.isPending || updateUser.isPending;

  // E10-F04-T10: the saved signature is NOT in the list DTO (it carries the logo as
  // a ~256 KB `data:` URI, so the list must not multiply it by the row count) — the
  // open Edit dialog fetches the one it is about to show. Until it lands the editor
  // is a blank PLACEHOLDER, not a value: `signatureLoaded` stays false and
  // `bodyFromForm` omits `email_signature`, so saving an unrelated field (a role
  // change) can never write that blank over the stored signature.
  const signatureQuery = useUserSignature(isEdit ? user.id : null);
  const loadedSignature = signatureQuery.data;
  useEffect(() => {
    if (loadedSignature === undefined) return;
    setForm((prev) => applyLoadedSignature(prev, loadedSignature));
  }, [loadedSignature]);
  const signatureLoading = isEdit && signatureQuery.isLoading;
  const signatureFailed = isEdit && signatureQuery.isError;

  // Team options = fetched team names. Keep the user's current team selectable even
  // if it is missing from the list (e.g. a renamed/stale team) so an Edit never
  // silently drops the assignment.
  const teamNames = useMemo(() => (teamsQuery.data ?? []).map((t) => t.name), [teamsQuery.data]);
  const teamOptions = useMemo(() => {
    const current = form.teamId.trim();
    if (current && !teamNames.includes(current)) return [current, ...teamNames];
    return teamNames;
  }, [teamNames, form.teamId]);

  function patch<K extends keyof UserForm>(key: K, value: UserForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function patchMailbox<K extends keyof UserMailboxForm>(key: K, value: UserMailboxForm[K]): void {
    setForm((prev) => ({ ...prev, mailbox: { ...prev.mailbox, [key]: value } }));
  }

  /**
   * A hand edit of the signature (E10-F04-T10): goes through `editedSignature`, which
   * also marks the value authoritative — a late fetch response can no longer replace
   * what the admin just typed, and a deliberate CLEAR is actually saved.
   */
  function patchSignature(html: string): void {
    setForm((prev) => editedSignature(prev, html));
  }

  function handleSave(): void {
    const name = form.name.trim();
    const email = form.email.trim();
    // Mirror the legacy "Name and email are required" guard before the request.
    if (!name || !email) {
      toast.error('Name and email are required');
      return;
    }
    // Legacy: a NEW user must have a password ("Password is required for new user").
    if (!isEdit && !form.password) {
      toast.error('Password is required for new user');
      return;
    }

    const body = bodyFromForm(form);
    const onSuccess = (): void => {
      toast.success(isEdit ? 'User updated' : 'User created');
      onClose();
    };
    const onError = (error: unknown): void => {
      toast.error(errorMessage(error, 'Save error'));
    };

    if (isEdit && user) {
      updateUser.mutate({ id: user.id, patch: body }, { onSuccess, onError });
    } else {
      createUser.mutate(body, { onSuccess, onError });
    }
  }

  const mb = form.mailbox;

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit User' : 'Add User'}
      maxWidth="md"
      testId="user-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="user-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="user-field-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Name *</span>
          <input
            id="user-field-name"
            data-testid="user-field-name"
            type="text"
            className={FIELD_CLASS}
            value={form.name}
            onChange={(e) => patch('name', e.target.value)}
          />
        </label>

        <label htmlFor="user-field-email" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Email *</span>
          <input
            id="user-field-email"
            data-testid="user-field-email"
            type="email"
            className={FIELD_CLASS}
            value={form.email}
            onChange={(e) => patch('email', e.target.value)}
          />
        </label>

        <label htmlFor="user-field-password" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">
            {isEdit ? 'Password' : 'Password *'}
          </span>
          <input
            id="user-field-password"
            data-testid="user-field-password"
            type="text"
            className={FIELD_CLASS}
            value={form.password}
            placeholder={isEdit ? 'Leave empty to keep current' : 'Password'}
            onChange={(e) => patch('password', e.target.value)}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label htmlFor="user-field-role" className="block">
            <span className="mb-1 block text-xs font-medium text-text-strong">Role *</span>
            <select
              id="user-field-role"
              data-testid="user-field-role"
              className={FIELD_CLASS}
              value={form.role}
              onChange={(e) => patch('role', e.target.value as UserForm['role'])}
            >
              {ADMIN_USER_ROLES.map((value) => (
                <option key={value} value={value}>
                  {roleLabel(value)}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor="user-field-team" className="block">
            <span className="mb-1 block text-xs font-medium text-text-strong">Team</span>
            <select
              id="user-field-team"
              data-testid="user-field-team"
              className={FIELD_CLASS}
              value={form.teamId}
              onChange={(e) => patch('teamId', e.target.value)}
            >
              <option value="">— No team —</option>
              {teamOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label htmlFor="user-field-avatar" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Avatar (initials)</span>
          <input
            id="user-field-avatar"
            data-testid="user-field-avatar"
            type="text"
            maxLength={3}
            className={FIELD_CLASS}
            value={form.avatar}
            placeholder="JS"
            onChange={(e) => patch('avatar', e.target.value)}
          />
        </label>

        <div className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Email Signature</span>
          {/* Этап 4 / KS-06: подписи «прям очень критичны» и должны уметь логотип —
              markdown его не выражал. Картинка хранится как data: и превращается в
              cid:-вложение при отправке (@eer/shared/inlineImages). */}
          <RichTextEditor
            testId="user-field-signature"
            ariaLabel="Email Signature"
            minHeightClass="min-h-24"
            value={form.emailSignature}
            // Read-only while the saved signature is still on the wire: typing into a
            // field that is about to be replaced by the response would lose the typing.
            disabled={signatureLoading}
            onChange={patchSignature}
            onInsertImage={(rejection) => {
              if (rejection) toast.error(rejection);
            }}
          />
          {signatureLoading ? (
            <span
              data-testid="user-field-signature-loading"
              className="mt-1 block text-[11px] text-gray-500"
            >
              Loading the saved signature…
            </span>
          ) : null}
          {signatureFailed ? (
            <span
              data-testid="user-field-signature-error"
              className="mt-1 block text-[11px] text-red-500"
            >
              Could not load the saved signature — it is kept as is unless you type a new one.
            </span>
          ) : null}
          <span className="mt-1 block text-[11px] text-gray-500">
            Appended automatically to outgoing emails (pre-kickoff, follow-up). A logo travels
            inside the message as an attachment.
          </span>
        </div>

        {/* Collapsible IMAP/SMTP "Email account" section (T04). */}
        <div className="rounded border border-border">
          <button
            type="button"
            data-testid="user-mailbox-toggle"
            aria-expanded={mailboxOpen}
            onClick={() => setMailboxOpen((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-text-strong"
          >
            <span>
              <i className="fa-solid fa-envelope mr-2" aria-hidden />
              Mailbox Settings
            </span>
            <span aria-hidden>{mailboxOpen ? '−' : '+'}</span>
          </button>

          {mailboxOpen ? (
            <div data-testid="user-mailbox-fields" className="grid gap-3 px-3 pb-3">
              <div className="grid grid-cols-2 gap-3">
                <label htmlFor="user-field-imap-host" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">IMAP Host</span>
                  <input
                    id="user-field-imap-host"
                    data-testid="user-field-imap-host"
                    type="text"
                    className={FIELD_CLASS}
                    value={mb.imapHost}
                    placeholder="imap.example.com"
                    onChange={(e) => patchMailbox('imapHost', e.target.value)}
                  />
                </label>
                <label htmlFor="user-field-imap-port" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">IMAP Port</span>
                  <input
                    id="user-field-imap-port"
                    data-testid="user-field-imap-port"
                    type="number"
                    className={FIELD_CLASS}
                    value={mb.imapPort}
                    placeholder="993"
                    onChange={(e) => patchMailbox('imapPort', e.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label htmlFor="user-field-imap-user" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">IMAP User</span>
                  <input
                    id="user-field-imap-user"
                    data-testid="user-field-imap-user"
                    type="text"
                    className={FIELD_CLASS}
                    value={mb.imapUser}
                    onChange={(e) => patchMailbox('imapUser', e.target.value)}
                  />
                </label>
                <label htmlFor="user-field-imap-password" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">IMAP Password</span>
                  <input
                    id="user-field-imap-password"
                    data-testid="user-field-imap-password"
                    type="password"
                    className={FIELD_CLASS}
                    value={mb.imapPassword}
                    placeholder={isEdit ? 'Leave empty to keep current' : ''}
                    onChange={(e) => patchMailbox('imapPassword', e.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label htmlFor="user-field-smtp-host" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">SMTP Host</span>
                  <input
                    id="user-field-smtp-host"
                    data-testid="user-field-smtp-host"
                    type="text"
                    className={FIELD_CLASS}
                    value={mb.smtpHost}
                    placeholder="(defaults to IMAP host)"
                    onChange={(e) => patchMailbox('smtpHost', e.target.value)}
                  />
                </label>
                <label htmlFor="user-field-smtp-port" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">SMTP Port</span>
                  <input
                    id="user-field-smtp-port"
                    data-testid="user-field-smtp-port"
                    type="number"
                    className={FIELD_CLASS}
                    value={mb.smtpPort}
                    placeholder="465"
                    onChange={(e) => patchMailbox('smtpPort', e.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label htmlFor="user-field-smtp-user" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">SMTP User</span>
                  <input
                    id="user-field-smtp-user"
                    data-testid="user-field-smtp-user"
                    type="text"
                    className={FIELD_CLASS}
                    value={mb.smtpUser}
                    placeholder="(defaults to IMAP user)"
                    onChange={(e) => patchMailbox('smtpUser', e.target.value)}
                  />
                </label>
                <label htmlFor="user-field-smtp-password" className="block">
                  <span className="mb-1 block text-[11px] text-gray-500">SMTP Password</span>
                  <input
                    id="user-field-smtp-password"
                    data-testid="user-field-smtp-password"
                    type="password"
                    className={FIELD_CLASS}
                    value={mb.smtpPassword}
                    placeholder={
                      isEdit ? 'Leave empty to keep current' : '(defaults to IMAP password)'
                    }
                    onChange={(e) => patchMailbox('smtpPassword', e.target.value)}
                  />
                </label>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// User row + tab
// ---------------------------------------------------------------------------

interface UserRowProps {
  readonly user: AdminUserDTO;
  readonly self: boolean;
  readonly onEdit: (user: AdminUserDTO) => void;
  readonly onDelete: (user: AdminUserDTO) => void;
}

function UserRow({ user, self, onEdit, onDelete }: UserRowProps): React.JSX.Element {
  return (
    <tr data-testid="user-row" data-user-id={user.id} className="border-t border-border">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            data-testid="user-avatar"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white"
          >
            {avatarLabel(user)}
          </span>
          <span data-testid="user-row-name" className="font-medium text-text-strong">
            {user.name || '—'}
            {self ? (
              <span data-testid="user-row-you" className="ml-1 text-xs text-gray-400">
                (you)
              </span>
            ) : null}
          </span>
        </div>
      </td>
      <td data-testid="user-row-email" className="px-3 py-2 text-sm text-gray-600">
        {user.email || '—'}
      </td>
      <td className="px-3 py-2">
        <RoleBadge role={user.role} />
      </td>
      <td data-testid="user-row-team" className="px-3 py-2 text-sm text-gray-600">
        {user.team_id || '—'}
      </td>
      <td className="px-3 py-2 text-center">
        {/* E08-F01-T06: Connect / Disconnect + status for ANOTHER user (admin).
            Connected state comes from get_users (has_outlook/outlook_email);
            OutlookConnect owns the OAuth popup + the disconnect confirm/POST. */}
        <OutlookConnect
          userId={user.id}
          hasOutlook={user.has_outlook}
          outlookEmail={user.outlook_email}
          variant="inline"
          testId={`user-outlook-${user.id}`}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          data-testid="user-row-edit"
          onClick={() => onEdit(user)}
          className="mr-3 text-sm text-gray-400 hover:text-primary"
          aria-label="Edit user"
          title="Edit user"
        >
          <i className="fa-solid fa-pen" aria-hidden />
        </button>
        {!self ? (
          <button
            type="button"
            data-testid="user-row-delete"
            onClick={() => onDelete(user)}
            className="text-sm text-gray-400 hover:text-danger"
            aria-label="Delete user"
            title="Delete user"
          >
            <i className="fa-solid fa-trash" aria-hidden />
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export interface UsersTabProps {
  /** The signed-in principal's id (`useAuth().user?.id`) — drives the (you)/Delete rules. */
  readonly currentUserId?: string | null;
}

/**
 * The Users admin tab. Lists every user (server orders created_at ASC) and offers
 * Add / Edit / Delete plus Outlook disconnect. The current user's own row shows
 * `(you)` and hides Delete (T03 acceptance #2).
 */
export function UsersTab({ currentUserId }: UsersTabProps): React.JSX.Element {
  const query = useUsers();
  const deleteUser = useDeleteUser();
  const toast = useToast();
  const [editing, setEditing] = useState<AdminUserDTO | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const users = useMemo(() => query.data ?? [], [query.data]);

  function openAdd(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(user: AdminUserDTO): void {
    setEditing(user);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  function handleDelete(user: AdminUserDTO): void {
    deleteUser.mutate(user.id, {
      onSuccess: () => toast.success('User deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete error')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="users-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div data-testid="users-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load users')}
      </div>
    );
  }

  return (
    <div data-testid="users-tab">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">User Management</h2>
        <button
          type="button"
          data-testid="users-add"
          onClick={openAdd}
          className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          <i className="fa-solid fa-plus mr-1" aria-hidden /> Add
        </button>
      </div>

      {users.length === 0 ? (
        <div data-testid="users-empty" className="py-4 text-center text-gray-400">
          No users
        </div>
      ) : (
        <table data-testid="users-table" className="w-full border-collapse">
          <thead>
            <tr className="text-left text-xs font-medium tracking-wide text-gray-500">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Team</th>
              <th className="px-3 py-2 text-center">Outlook</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                self={isSelf(u.id, currentUserId)}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            ))}
          </tbody>
        </table>
      )}

      {dialogOpen ? <UserDialog user={editing} onClose={closeDialog} /> : null}
    </div>
  );
}

export default UsersTab;
